import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import ResponsiveContainer from '@/components/ResponsiveContainer';
import TableResponsive from '@/components/TableResponsive';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { toast } from '../utils/toast';
import { Plus, Edit, XCircle, Search, Users, Briefcase, Building2, Shield, Key, Copy, BookOpen, Loader2, RotateCcw, Archive, MoreVertical } from 'lucide-react';
import axios from 'axios';
import { sortObjectOptions } from '@/utils/sortOptions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StaffManagementPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const canManageStaffConfig = user && ['Hospital Admin', 'Super Admin', 'Exlva Admin'].includes(user.role);
  const canViewStaffTopics = canManageStaffConfig || ['Manager', 'Reporting Manager', 'ReportingManager'].includes(user?.role) || user?.is_reporting_manager;
  
  // Tab state
  const [activeTab, setActiveTab] = useState('departments'); // departments, job_functions, staff
  
  // Departments
  const [departments, setDepartments] = useState([]);
  const [departmentModalOpen, setDepartmentModalOpen] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [departmentForm, setDepartmentForm] = useState({ name: '', description: '' });
  
  // Job Roles
  const [jobFunctions, setJobFunctions] = useState([]);
  const [jobFunctionModalOpen, setJobFunctionModalOpen] = useState(false);
  const [editingJobFunction, setEditingJobFunction] = useState(null);
  const [jobFunctionForm, setJobFunctionForm] = useState({ 
    name: '', 
    description: '', 
    department_id: '', 
    assigned_topics: []  // New format: [{topic_id, topic_title, section_ids}]
  });
  
  // Topics for assignment
  const [availableTopics, setAvailableTopics] = useState([]);

  // Staff
  const [staff, setStaff] = useState([]);
  const [staffModalOpen, setStaffModalOpen] = useState(false);
  const [credentialsModalOpen, setCredentialsModalOpen] = useState(false);
  const [selectedCredentials, setSelectedCredentials] = useState({ username: '', password: '', employee_name: '' });
  const [topicsModalOpen, setTopicsModalOpen] = useState(false);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [selectedStaffTopics, setSelectedStaffTopics] = useState({ employee_name: '', job_function_name: '', assigned_topics: [] });
  const [selectedStaffEmployeeId, setSelectedStaffEmployeeId] = useState(null);
  const [resettingTopicId, setResettingTopicId] = useState(null);
  const [editingStaff, setEditingStaff] = useState(null);
  const [staffForm, setStaffForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    employee_id: '',
    department_id: '',
    job_function_id: '',
    reporting_manager_id: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentSearchTerm, setDepartmentSearchTerm] = useState('');
  const [jobRoleSearchTerm, setJobRoleSearchTerm] = useState('');
  
  const fetchTopics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/training/content`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableTopics(response.data || []);
    } catch (error) {
      console.error('Failed to fetch topics:', error);
    }
  };
  
  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/hospital-config/departments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDepartments(response.data.departments || []);
    } catch (error) {
      console.error('Failed to fetch departments:', error);
    }
  };
  
  const fetchJobFunctions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/hospital-config/job-functions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobFunctions(response.data.job_functions || []);
    } catch (error) {
      console.error('Failed to fetch job roles:', error);
    }
  };
  
  const fetchStaff = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/hospital-config/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStaff(response.data.employees || []);
    } catch (error) {
      console.error('Failed to fetch staff:', error);
    }
  };
  
  const fetchAllData = async () => {
    await Promise.all([fetchDepartments(), fetchJobFunctions(), fetchStaff(), fetchTopics()]);
    setLoading(false);
  };
  
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      toast.error('Please sign in to access this page');
      navigate('/signin');
      return;
    }
    
    try {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      const isManagerViewer = ['Manager', 'Reporting Manager', 'ReportingManager'].includes(parsedUser.role) || parsedUser.is_reporting_manager;
      if (!['Hospital Admin', 'Super Admin', 'Exlva Admin'].includes(parsedUser.role) && !isManagerViewer) {
        toast.error('Access denied');
        navigate('/dashboard');
        return;
      }
      if (isManagerViewer && !['Hospital Admin', 'Super Admin', 'Exlva Admin'].includes(parsedUser.role)) {
        setActiveTab('staff');
      }
      
      fetchAllData();
    } catch (error) {
      navigate('/signin');
    }
  }, [navigate]);
  
  // Departments CRUD
  
  const handleAddDepartment = () => {
    setEditingDepartment(null);
    setDepartmentForm({ name: '', description: '' });
    setDepartmentModalOpen(true);
  };
  
  const handleEditDepartment = (dept) => {
    setEditingDepartment(dept);
    setDepartmentForm({ name: dept.name, description: dept.description || '' });
    setDepartmentModalOpen(true);
  };
  
  const handleSaveDepartment = async () => {
    try {
      // Validation
      if (!departmentForm.name || departmentForm.name.trim() === '') {
        toast.error('Department name is required');
        return;
      }
      
      const token = localStorage.getItem('token');
      if (editingDepartment) {
        await axios.put(`${API}/hospital-config/departments/${editingDepartment.department_id}`,
          departmentForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Department updated successfully');
      } else {
        await axios.post(`${API}/hospital-config/departments`, departmentForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Department created successfully');
      }
      setDepartmentModalOpen(false);
      fetchDepartments();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to save department';
      toast.error(errorMessage);
      console.error('Department save error:', error.response?.data || error);
    }
  };
  
  const handleDeleteDepartment = async (deptId) => {
    if (!window.confirm('Are you sure you want to delete this department?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/hospital-config/departments/${deptId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Department deleted successfully');
      fetchDepartments();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to delete department';
      toast.error(errorMessage);
      console.error('Department delete error:', error.response?.data || error);
    }
  };
  
  // Job Roles CRUD
  
  const handleAddJobFunction = () => {
    setEditingJobFunction(null);
    setJobFunctionForm({ name: '', description: '', department_id: '', assigned_topics: [] });
    setJobFunctionModalOpen(true);
  };
  
  // Helper function to get a unique identifier for a section
  const getSectionId = (section) => {
    // Use section_title as the canonical identifier since section.id is often undefined
    return section.section_title || section.title || section.id || '';
  };

  // Helper to check if a stored section_id matches a section (handles legacy UUID data)
  const sectionMatchesStoredId = (section, storedId) => {
    const sectionTitle = section.section_title || section.title || '';
    const sectionId = section.id || '';
    const storedIdStr = String(storedId || '');
    
    // Match by section_title (new format) OR by id (legacy UUID format)
    return sectionTitle === storedIdStr || sectionId === storedIdStr;
  };

  // Migrate old section_ids (UUIDs) to new format (section_titles) when loading for edit
  const migrateAssignedTopics = (assignedTopics) => {
    if (!assignedTopics || !Array.isArray(assignedTopics)) return [];
    
    return assignedTopics.map(at => {
      const topicIdStr = String(at.topic_id);
      const topic = availableTopics.find(t => String(t.id) === topicIdStr);
      
      if (!topic || !topic.sections) {
        // Topic not found, keep as-is
        return at;
      }
      
      const oldSectionIds = at.section_ids || [];
      
      // Check if section_ids are already in new format (section_titles)
      const firstId = oldSectionIds[0];
      const isNewFormat = topic.sections.some(s => 
        (s.section_title || s.title) === firstId
      );
      
      if (isNewFormat || oldSectionIds.length === 0) {
        return at; // Already migrated or empty
      }
      
      // Migrate: find sections that match old UUIDs and convert to section_titles
      const migratedSectionIds = [];
      topic.sections.forEach(section => {
        const sectionTitle = section.section_title || section.title;
        const sectionId = section.id;
        
        // Check if this section was selected (by UUID or title)
        const wasSelected = oldSectionIds.some(oldId => 
          String(oldId) === String(sectionId) || String(oldId) === sectionTitle
        );
        
        if (wasSelected && sectionTitle) {
          migratedSectionIds.push(sectionTitle);
        }
      });
      
      // If migration found no matches but there were old IDs, 
      // it might be truly legacy data - select all sections as fallback
      // OR keep empty if that's preferred
      const finalSectionIds = migratedSectionIds.length > 0 
        ? migratedSectionIds 
        : (oldSectionIds.length > 0 
            ? topic.sections.map(s => getSectionId(s)) // Fallback: select all if migration failed
            : []);
      
      return {
        ...at,
        section_ids: finalSectionIds
      };
    });
  };

  const handleEditJobFunction = (jf) => {
    setEditingJobFunction(jf);
    
    // Migrate old section_ids format to new format when loading
    const migratedTopics = migrateAssignedTopics(jf.assigned_topics || []);
    
    setJobFunctionForm({ 
      name: jf.name, 
      description: jf.description || '', 
      department_id: jf.department_id,
      assigned_topics: migratedTopics
    });
    setJobFunctionModalOpen(true);
  };

  const handleToggleTopic = (topicId) => {
    const topicIdStr = String(topicId);
    const topic = availableTopics.find(t => String(t.id) === topicIdStr);
    if (!topic) return;
    
    setJobFunctionForm(prevForm => {
      const isTopicCurrentlySelected = prevForm.assigned_topics.some(at => String(at.topic_id) === topicIdStr);
      
      if (isTopicCurrentlySelected) {
        // Remove topic
        return {
          ...prevForm,
          assigned_topics: prevForm.assigned_topics.filter(at => String(at.topic_id) !== topicIdStr)
        };
      } else {
        // Add topic with all sections selected - use section_title as ID since id may be undefined
        const allSectionIds = (topic.sections || []).map(s => getSectionId(s));
        const newTopicMapping = {
          topic_id: topicIdStr,
          topic_title: topic.title,
          section_ids: allSectionIds
        };
        
        return {
          ...prevForm,
          assigned_topics: [...prevForm.assigned_topics, newTopicMapping]
        };
      }
    });
  };
  
  const handleToggleSectionInTopic = (e, topicId, sectionId) => {
    const topicIdStr = String(topicId);
    
    setJobFunctionForm(prevForm => {
      const updatedTopics = prevForm.assigned_topics.map(at => {
        if (String(at.topic_id) === topicIdStr) {
          const currentSections = at.section_ids || [];
          const hasSection = currentSections.includes(sectionId);
          
          // Toggle: remove if exists, add if doesn't
          const newSections = hasSection
            ? currentSections.filter(sid => sid !== sectionId)
            : [...currentSections, sectionId];
          
          return { ...at, section_ids: newSections };
        }
        return at;
      });
      
      return {
        ...prevForm,
        assigned_topics: updatedTopics
      };
    });
  };
  
  const isTopicSelected = (topicId) => {
    const topicIdStr = String(topicId);
    return jobFunctionForm.assigned_topics.some(at => String(at.topic_id) === topicIdStr);
  };
  
  const getSelectedSectionIds = (topicId) => {
    const topicIdStr = String(topicId);
    const assignedTopic = jobFunctionForm.assigned_topics.find(at => String(at.topic_id) === topicIdStr);
    const storedIds = assignedTopic?.section_ids || [];
    
    // Filter to only include IDs that match actual sections in the topic
    // This prevents counts like "5 of 4" when stale/legacy IDs exist
    const topic = availableTopics.find(t => String(t.id) === topicIdStr);
    if (!topic || !topic.sections) return storedIds;
    
    const validSectionIds = new Set(topic.sections.map(s => getSectionId(s)));
    return storedIds.filter(id => validSectionIds.has(id));
  };
  
  const handleSaveJobFunction = async () => {
    try {
      // Validation
      if (!jobFunctionForm.name || jobFunctionForm.name.trim() === '') {
        toast.error('Job role name is required');
        return;
      }
      
      const token = localStorage.getItem('token');
      if (editingJobFunction) {
        const response = await axios.put(`${API}/hospital-config/job-functions/${editingJobFunction.job_function_id}`,
          jobFunctionForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // Show appropriate message based on whether an override was created
        if (response.data.is_override) {
          toast.success('Hospital-specific job role created. Your changes apply only to this hospital.');
        } else {
          toast.success('Job role updated successfully');
        }
      } else {
        await axios.post(`${API}/hospital-config/job-functions`, jobFunctionForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Job role created successfully');
      }
      setJobFunctionModalOpen(false);
      fetchJobFunctions();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to save job role';
      toast.error(errorMessage);
      console.error('Job role save error:', error.response?.data || error);
    }
  };
  
  const handleDeleteJobFunction = async (jfId) => {
    if (!window.confirm('Are you sure you want to delete this job role?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/hospital-config/job-functions/${jfId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Job role deleted successfully');
      fetchJobFunctions();
    } catch (error) {
      toast.error('Failed to delete job role');
    }
  };
  
  // Staff CRUD
  
  const handleAddStaff = async () => {
    setEditingStaff(null);
    
    // Fetch auto-generated employee ID
    let generatedEmployeeId = '';
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/hospital-config/employees/generate-id`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      generatedEmployeeId = response.data.employee_id;
    } catch (error) {
      console.error('Failed to generate employee ID:', error);
      // If generation fails, leave it empty - user can enter manually
    }
    
    setStaffForm({
      full_name: '',
      email: '',
      phone: '',
      employee_id: generatedEmployeeId,  // Pre-fill with generated ID
      department_id: '',
      job_function_id: '',
      reporting_manager_id: ''
    });
    setStaffModalOpen(true);
  };
  
  const handleEditStaff = (emp) => {
    setEditingStaff(emp);
    setStaffForm({
      full_name: emp.full_name,
      email: emp.email,
      phone: emp.phone || '',
      employee_id: emp.employee_id,
      department_id: emp.department_id,
      job_function_id: emp.job_function_id,
      reporting_manager_id: emp.reporting_manager_id || ''
    });
    setStaffModalOpen(true);
  };
  
  const handleSaveStaff = async () => {
    try {
      // Validation
      if (!staffForm.employee_id || staffForm.employee_id.trim() === '') {
        toast.error('Staff ID is required');
        return;
      }
      
      if (!staffForm.full_name || staffForm.full_name.trim() === '') {
        toast.error('Staff name is required');
        return;
      }
      
      if (!staffForm.email || staffForm.email.trim() === '') {
        toast.error('Email is required');
        return;
      }
      
      const token = localStorage.getItem('token');
      const submitData = {
        ...staffForm,
        phone: staffForm.phone || null,
        reporting_manager_id: staffForm.reporting_manager_id || null
      };
      
      if (editingStaff) {
        await axios.put(`${API}/hospital-config/employees/${editingStaff.employee_id}`,
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Staff updated successfully');
      } else {
        await axios.post(`${API}/hospital-config/employees`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Staff added successfully');
      }
      setStaffModalOpen(false);
      fetchStaff();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to save staff';
      toast.error(errorMessage);
      console.error('Staff save error:', error.response?.data || error);
    }
  };
  
  const handleViewCredentials = async (emp) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/hospital-config/employees/${emp.employee_id}/credentials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedCredentials({
        username: response.data.username,
        password: response.data.password,
        employee_name: response.data.employee_name,
        employee_id: response.data.employee_id
      });
      setCredentialsModalOpen(true);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to fetch credentials';
      toast.error(errorMessage);
      console.error('Credentials fetch error:', error.response?.data || error);
    }
  };
  
  const handleCopyToClipboard = (text, fieldName) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${fieldName} copied to clipboard`);
    }).catch(() => {
      toast.error('Failed to copy to clipboard');
    });
  };
  
  const handleViewTopics = async (emp) => {
    setTopicsLoading(true);
    setSelectedStaffTopics({ employee_name: emp.full_name || '', job_function_name: '', assigned_topics: [] });
    setSelectedStaffEmployeeId(emp.employee_id);
    setTopicsModalOpen(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/hospital-config/employees/${emp.employee_id}/topics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedStaffTopics({
        employee_name: response.data.employee_name,
        job_function_name: response.data.job_function_name,
        assigned_topics: response.data.assigned_topics || []
      });
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to fetch assigned topics';
      toast.error(errorMessage);
      console.error('Topics fetch error:', error.response?.data || error);
    } finally {
      setTopicsLoading(false);
    }
  };
  
  const handleDeleteStaff = async (emp) => {
    const completedChapterCount = Number(emp?.completed_chapter_count || 0);
    if (completedChapterCount > 0) {
      toast.error('This participant cannot be deleted because they have already completed one or more chapters. You may deactivate the participant instead.');
      return;
    }

    if (!window.confirm("Are you sure you want to permanently delete this participant? This action will permanently delete the participant's profile and all associated records. This action cannot be undone.")) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/hospital-config/employees/${emp.employee_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data?.message || 'Staff deleted successfully');
      fetchStaff();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to delete staff';
      toast.error(errorMessage);
    }
  };

  const handleToggleArchiveStaff = async (emp) => {
    const isArchived = emp.status === 'Archived' || emp.is_archived;
    const action = isArchived ? 'unarchive' : 'archive';
    if (!window.confirm(`Are you sure you want to ${action} this staff member?`)) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(`${API}/hospital/employees/${emp.employee_id}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data?.message || `Staff ${action}d successfully`);
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} staff`);
    }
  };
  
  // Filtered staff
  const filteredStaff = staff.filter(emp =>
    emp.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.employee_id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filtered departments
  const filteredDepartments = departments.filter(dept =>
    dept.name.toLowerCase().includes(departmentSearchTerm.toLowerCase())
  );

  // Filtered job roles
  const filteredJobRoles = jobFunctions.filter(jf =>
    jf.name.toLowerCase().includes(jobRoleSearchTerm.toLowerCase())
  );
  
  if (loading || !user) {
    return (
      <AdminLayout>
        <ResponsiveContainer>
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">Loading...</p>
            </div>
          </div>
        </ResponsiveContainer>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <ResponsiveContainer>
        {/* Page Title - Role-based */}
        <h1 className="text-xl md:text-2xl lg:text-2xl font-bold text-gray-900 mb-4 md:mb-6">
          {user && ['Super Admin', 'Exlva Admin', 'Admin'].includes(user.role) 
            ? 'DEFAULT ORG WORKFLOW' 
            : 'STAFF MANAGEMENT'}
        </h1>

        {/* Statistics Cards - Responsive */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 lg:gap-6 mb-6 md:mb-8">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-blue-700 mb-2">Total Departments</p>
                <h3 className="text-4xl font-bold text-blue-900">{departments.length}</h3>
              </div>
              <div className="w-12 h-12 bg-blue-200 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-blue-700" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-purple-700 mb-2">Job Roles</p>
                <h3 className="text-4xl font-bold text-purple-900">{jobFunctions.length}</h3>
              </div>
              <div className="w-12 h-12 bg-purple-200 rounded-lg flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-purple-700" />
              </div>
            </div>
          </div>
          {/* Employees card - Only for Hospital Admin */}
          {user?.role === 'Hospital Admin' && (
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm font-medium text-green-700 mb-2">Total Staff</p>
                  <h3 className="text-4xl font-bold text-green-900">{staff.length}</h3>
                </div>
                <div className="w-12 h-12 bg-green-200 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-green-700" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-md mb-6">
          <div className="border-b border-gray-200">
            <div className="flex space-x-4 px-6">
              <button
                onClick={() => setActiveTab('departments')}
                className={`py-4 px-4 font-medium border-b-2 transition-colors ${
                  activeTab === 'departments'
                    ? 'border-[#0056B3] text-[#0056B3]'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Departments
              </button>
              <button
                onClick={() => setActiveTab('job_functions')}
                className={`py-4 px-4 font-medium border-b-2 transition-colors ${
                  activeTab === 'job_functions'
                    ? 'border-[#0056B3] text-[#0056B3]'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Job Roles
              </button>
              {/* Staff tab - Only Hospital Admin and managers can see staff */}
              {user?.role === 'Hospital Admin' && (
                <button
                  onClick={() => setActiveTab('staff')}
                  className={`py-4 px-4 font-medium border-b-2 transition-colors ${
                    activeTab === 'staff'
                      ? 'border-[#0056B3] text-[#0056B3]'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Staff
                </button>
              )}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Departments Tab */}
            {activeTab === 'departments' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-[18px] font-semibold">Departments</h2>
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Search departments..."
                        value={departmentSearchTerm}
                        onChange={(e) => setDepartmentSearchTerm(e.target.value)}
                        className="pl-10 w-64"
                      />
                    </div>
                    <Button onClick={handleAddDepartment} className="bg-[#0056B3] hover:bg-[#0056B3]/90">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Department
                    </Button>
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDepartments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center py-8 text-gray-500">
                          {departmentSearchTerm ? 'No departments match your search.' : 'No departments found. Add your first department to get started.'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortObjectOptions(filteredDepartments, 'name').map((dept) => (
                        <TableRow key={dept.department_id}>
                          <TableCell className="font-medium">
                            <div className="flex items-center space-x-2">
                              <span>{dept.name}</span>
                              {dept.is_default && (
                                <Badge variant="secondary" className="bg-blue-100 text-blue-700">Default</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{dept.description || '-'}</TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end gap-1 min-w-[72px]">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleEditDepartment(dept)}
                                disabled={dept.is_default && user?.role === 'Hospital Admin'}
                                data-tooltip={dept.is_default && user?.role === 'Hospital Admin' ? 'Default departments cannot be edited' : 'Edit Department'}
                                aria-label="Edit Department"
                                className="h-9 w-9 shrink-0 p-0"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    data-tooltip="More actions"
                                    aria-label="More department actions"
                                    className="h-9 w-9 shrink-0 p-0"
                                  >
                                    <MoreVertical className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" sideOffset={6} className="w-48">
                                  <DropdownMenuItem
                                    onClick={() => !(dept.is_default && user?.role === 'Hospital Admin') && handleDeleteDepartment(dept.department_id)}
                                    disabled={dept.is_default && user?.role === 'Hospital Admin'}
                                    className="text-red-600 focus:text-red-700"
                                  >
                                    <XCircle className="mr-2 h-4 w-4" />
                                    Delete Department
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Job Roles Tab */}
            {activeTab === 'job_functions' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-[18px] font-semibold">Job Roles</h2>
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Search job roles..."
                        value={jobRoleSearchTerm}
                        onChange={(e) => setJobRoleSearchTerm(e.target.value)}
                        className="pl-10 w-64"
                      />
                    </div>
                    <Button onClick={handleAddJobFunction} className="bg-[#0056B3] hover:bg-[#0056B3]/90">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Job Role
                    </Button>
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Topics Assigned</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredJobRoles.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                          {jobRoleSearchTerm ? 'No job roles match your search.' : 'No job roles found. Add your first job role to get started.'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortObjectOptions(filteredJobRoles, 'name').map((jf) => (
                        <TableRow key={jf.job_function_id}>
                          <TableCell className="font-medium">
                            <div className="flex items-center space-x-2">
                              <span>{jf.name}</span>
                              {jf.is_default && (
                                <Badge variant="secondary" className="bg-blue-100 text-blue-700">Default</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {departments.find(d => d.department_id === jf.department_id)?.name || '-'}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {(jf.assigned_topics || jf.assigned_topic_ids || []).length} Topics
                            </Badge>
                          </TableCell>
                          <TableCell>{jf.description || '-'}</TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end gap-1 min-w-[72px]">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleEditJobFunction(jf)}
                                data-tooltip={jf.is_default && user?.role === 'Hospital Admin' ? 'Edit will create a hospital-specific copy' : 'Edit job role'}
                                aria-label="Edit Job Role"
                                className="h-9 w-9 shrink-0 p-0"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    data-tooltip="More actions"
                                    aria-label="More job role actions"
                                    className="h-9 w-9 shrink-0 p-0"
                                  >
                                    <MoreVertical className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" sideOffset={6} className="w-48">
                                  <DropdownMenuItem
                                    onClick={() => !(jf.is_default && user?.role === 'Hospital Admin') && handleDeleteJobFunction(jf.job_function_id)}
                                    disabled={jf.is_default && user?.role === 'Hospital Admin'}
                                    className="text-red-600 focus:text-red-700"
                                  >
                                    <XCircle className="mr-2 h-4 w-4" />
                                    Delete Job Role
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Staff Tab */}
            {activeTab === 'staff' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-[18px] font-semibold">Staff</h2>
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Search staff..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 w-64"
                      />
                    </div>
                    {canManageStaffConfig && (
                      <Button onClick={handleAddStaff} className="bg-[#0056B3] hover:bg-[#0056B3]/90">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Staff
                      </Button>
                    )}
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Reporting Manager</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Job Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredStaff.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                          {searchTerm ? 'No staff match your search.' : 'No staff found. Add your first staff member to get started.'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredStaff.map((emp) => (
                        <TableRow key={emp.employee_id}>
                          <TableCell className="font-medium">{emp.full_name}</TableCell>
                          <TableCell>{emp.email}</TableCell>
                          <TableCell>
                            {emp.reporting_manager_name || 'Not Assigned'}
                          </TableCell>
                          <TableCell>
                            {departments.find(d => d.department_id === emp.department_id)?.name || emp.department_name || '-'}
                          </TableCell>
                          <TableCell>
                            {jobFunctions.find(jf => jf.job_function_id === emp.job_function_id)?.name || emp.job_function_name || '-'}
                          </TableCell>
                          <TableCell>
                            <Badge
                              className={
                                emp.status === 'Active'
                                  ? 'bg-green-100 text-green-800'
                                  : emp.status === 'Archived'
                                    ? 'bg-slate-100 text-slate-800'
                                    : 'bg-amber-100 text-amber-800'
                              }
                            >
                              {emp.status || emp.lifecycle_status || 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end gap-1 min-w-[88px]">
                              {canManageStaffConfig && (
                                <>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => handleEditStaff(emp)}
                                    data-tooltip="Edit Staff"
                                    aria-label="Edit Staff"
                                    className="h-9 w-9 shrink-0 p-0"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleViewTopics(emp)}
                                    data-tooltip="View Chapters"
                                    aria-label="View Chapters"
                                    className="hidden h-9 w-9 shrink-0 p-0 sm:inline-flex"
                                  >
                                    <BookOpen className="w-4 h-4 text-green-600" />
                                  </Button>
                                  {(() => {
                                    const completedChapterCount = Number(emp.completed_chapter_count || 0);
                                    const cannotDelete = completedChapterCount > 0;
                                    return (
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            data-tooltip="More actions"
                                            aria-label="More staff actions"
                                            className="h-9 w-9 shrink-0 p-0"
                                          >
                                            <MoreVertical className="w-4 h-4" />
                                          </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" sideOffset={6} className="w-56">
                                          <DropdownMenuItem className="sm:hidden" onClick={() => handleViewTopics(emp)}>
                                            <BookOpen className="mr-2 h-4 w-4 text-green-600" />
                                            View Chapters
                                          </DropdownMenuItem>
                                          <DropdownMenuItem onClick={() => handleViewCredentials(emp)}>
                                            <Key className="mr-2 h-4 w-4 text-blue-600" />
                                            View Login Credentials
                                          </DropdownMenuItem>
                                          <DropdownMenuItem onClick={() => handleToggleArchiveStaff(emp)}>
                                            {emp.status === 'Archived' || emp.is_archived ? (
                                              <RotateCcw className="mr-2 h-4 w-4 text-green-700" />
                                            ) : (
                                              <Archive className="mr-2 h-4 w-4 text-slate-700" />
                                            )}
                                            {emp.status === 'Archived' || emp.is_archived ? 'Unarchive Staff' : 'Archive Staff'}
                                          </DropdownMenuItem>
                                          <DropdownMenuSeparator />
                                          <DropdownMenuItem
                                            onClick={() => !cannotDelete && handleDeleteStaff(emp)}
                                            disabled={cannotDelete}
                                            className="text-red-600 focus:text-red-700"
                                          >
                                            <XCircle className="mr-2 h-4 w-4" />
                                            Delete Staff
                                          </DropdownMenuItem>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    );
                                  })()}
                                </>
                              )}
                              {!canManageStaffConfig && (
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => handleViewTopics(emp)}
                                  data-tooltip="View Chapters"
                                  aria-label="View Chapters"
                                  className="h-9 w-9 shrink-0 p-0"
                                >
                                  <BookOpen className="w-4 h-4 text-green-600" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>

        {/* Department Modal */}
        <Dialog open={departmentModalOpen} onOpenChange={setDepartmentModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingDepartment ? 'Edit Department' : 'Add Department'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Department Name *</Label>
                <Input
                  value={departmentForm.name}
                  onChange={(e) => setDepartmentForm({ ...departmentForm, name: e.target.value })}
                  placeholder="e.g., Cardiology"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  value={departmentForm.description}
                  onChange={(e) => setDepartmentForm({ ...departmentForm, description: e.target.value })}
                  placeholder="Brief description"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDepartmentModalOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveDepartment} className="bg-[#0056B3] hover:bg-[#0056B3]/90">
                {editingDepartment ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Job Role Modal */}
        <Dialog open={jobFunctionModalOpen} onOpenChange={setJobFunctionModalOpen}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle>{editingJobFunction ? 'Edit Job Role' : 'Add Job Role'}</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto min-h-0 mt-4">
            <div className="space-y-4">
              <div>
                <Label>Job Role Name *</Label>
                <Input
                  value={jobFunctionForm.name}
                  onChange={(e) => setJobFunctionForm({ ...jobFunctionForm, name: e.target.value })}
                  placeholder="e.g., Senior Nurse"
                />
              </div>
              <div>
                <Label>Department *</Label>
                <Select
                  value={jobFunctionForm.department_id}
                  onValueChange={(value) => setJobFunctionForm({ ...jobFunctionForm, department_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    {sortObjectOptions(departments, 'name').map((dept) => (
                      <SelectItem key={dept.department_id} value={dept.department_id}>
                        {dept.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  value={jobFunctionForm.description}
                  onChange={(e) => setJobFunctionForm({ ...jobFunctionForm, description: e.target.value })}
                  placeholder="Brief description"
                />
              </div>
              
              {/* Chapter & Subchapter Assignment - Unified View */}
              <div className="border-t pt-4">
                <Label className="text-base font-semibold mb-3 block">Assign Training Chapters & Subchapters</Label>
                <div className="text-sm text-gray-600 mb-3">
                  Check chapters to assign. When you select a chapter, all its subchapters are automatically selected. You can deselect specific subchapters if needed.
                </div>
                
                {/* Unified Chapters & Subchapters View */}
                <div className="border rounded-lg max-h-[450px] overflow-y-auto">
                  {availableTopics.length === 0 ? (
                    <div className="text-center text-gray-500 py-8">
                      No chapters available. Please create chapters first.
                    </div>
                  ) : (
                    <div className="divide-y">
                      {availableTopics.map((topic) => {
                        const isSelected = isTopicSelected(topic.id);
                        const selectedSectionIds = getSelectedSectionIds(topic.id);
                        const sections = topic.sections || [];
                        const allSectionsSelected = sections.length > 0 && selectedSectionIds.length === sections.length;

                        return (
                          <div key={topic.id} className="p-4 hover:bg-gray-50">
                            {/* Chapter Checkbox & Title */}
                            <div className="flex items-start space-x-3 mb-2">
                              <input
                                type="checkbox"
                                id={`topic-${topic.id}`}
                                checked={isSelected}
                                onChange={() => handleToggleTopic(topic.id)}
                                className="mt-1 h-4 w-4"
                              />
                              <div className="flex-1">
                                <label 
                                  htmlFor={`topic-${topic.id}`}
                                  className="font-semibold text-gray-900 cursor-pointer block"
                                >
                                  {topic.title}
                                </label>
                                <p className="text-sm text-gray-600 mt-1">{topic.description}</p>
                                {isSelected && (
                                  <div className="mt-2">
                                    <span className="text-xs font-medium text-blue-600">
                                      {selectedSectionIds.length} of {sections.length} subchapters selected
                                      {allSectionsSelected && ' (All)'}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {/* Subchapters (only shown if chapter is selected) */}
                            {isSelected && sections.length > 0 && (
                              <div className="ml-7 mt-3 space-y-2 border-l-2 border-blue-200 pl-4">
                                {sections.map((section, sectionIndex) => {
                                  // Use section_title as unique ID since section.id may be undefined
                                  const sectionId = getSectionId(section);
                                  const isChecked = selectedSectionIds.includes(sectionId);
                                  return (
                                    <div 
                                      key={sectionId || sectionIndex} 
                                      className="flex items-center space-x-2 py-1"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => handleToggleSectionInTopic(null, topic.id, sectionId)}
                                        className="h-4 w-4 cursor-pointer rounded border-gray-300"
                                      />
                                      <span 
                                        className="flex-1 cursor-pointer text-sm"
                                        onClick={() => handleToggleSectionInTopic(null, topic.id, sectionId)}
                                      >
                                        <span className="font-medium text-gray-800">{section.section_title || section.title}</span>
                                        {section.section_description && (
                                          <span className="text-gray-600 text-xs ml-2">{section.section_description}</span>
                                        )}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            
                            {isSelected && sections.length === 0 && (
                              <div className="ml-7 mt-2 text-sm text-gray-500 italic">
                                No subchapters defined for this chapter
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                
                {/* Summary */}
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-gray-700">
                      {jobFunctionForm.assigned_topics.length} {jobFunctionForm.assigned_topics.length === 1 ? 'Chapter' : 'Chapters'} Selected
                    </span>
                    <span className="text-gray-600">
                      {jobFunctionForm.assigned_topics.reduce((sum, t) => sum + (t.section_ids?.length || 0), 0)} Total Subchapters
                    </span>
                  </div>
                </div>
              </div>
            </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setJobFunctionModalOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveJobFunction} className="bg-[#0056B3] hover:bg-[#0056B3]/90">
                {editingJobFunction ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Staff Modal */}
        <Dialog open={staffModalOpen} onOpenChange={setStaffModalOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingStaff ? 'Edit Staff' : 'Add Staff'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Full Name *</Label>
                  <Input
                    value={staffForm.full_name}
                    onChange={(e) => setStaffForm({ ...staffForm, full_name: e.target.value })}
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <Label>Employee ID *</Label>
                  <Input
                    value={staffForm.employee_id}
                    onChange={(e) => setStaffForm({ ...staffForm, employee_id: e.target.value })}
                    placeholder="EMP001"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={staffForm.email}
                    onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })}
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={staffForm.phone}
                    onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })}
                    placeholder="+91 9876543210"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Department *</Label>
                  <Select
                    value={staffForm.department_id}
                    onValueChange={(value) => setStaffForm({ ...staffForm, department_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      {sortObjectOptions(departments, 'name').map((dept) => (
                        <SelectItem key={dept.department_id} value={dept.department_id}>
                          {dept.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Job Role *</Label>
                  <Select
                    value={staffForm.job_function_id}
                    onValueChange={(value) => setStaffForm({ ...staffForm, job_function_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select job role" />
                    </SelectTrigger>
                    <SelectContent>
                      {sortObjectOptions(jobFunctions
                        .filter(jf => !staffForm.department_id || jf.department_id === staffForm.department_id), 'name')
                        .map((jf) => (
                          <SelectItem key={jf.job_function_id} value={jf.job_function_id}>
                            {jf.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Reporting Manager (Optional)</Label>
                <Select
                  value={staffForm.reporting_manager_id || undefined}
                  onValueChange={(value) => setStaffForm({ ...staffForm, reporting_manager_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="None - No reporting manager" />
                  </SelectTrigger>
                  <SelectContent>
                    {sortObjectOptions(staff
                      .filter(emp => emp.employee_id !== editingStaff?.employee_id), 'full_name')
                      .map((emp) => (
                        <SelectItem key={emp.employee_id} value={emp.employee_id}>
                          {emp.full_name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStaffModalOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveStaff} className="bg-[#0056B3] hover:bg-[#0056B3]/90">
                {editingStaff ? 'Update' : 'Add'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Staff Credentials Modal */}
        <Dialog open={credentialsModalOpen} onOpenChange={setCredentialsModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Staff Login Credentials</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-500 mb-1">Employee Name</p>
                  <p className="text-base font-semibold text-gray-900">{selectedCredentials.employee_name}</p>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-500 mb-1">Username (Email)</p>
                  <p className="text-base font-mono text-gray-900">{selectedCredentials.username}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleCopyToClipboard(selectedCredentials.username, 'Username')}
                  className="ml-2"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-500 mb-1">Password</p>
                  <p className="text-base font-mono font-bold text-gray-900">{selectedCredentials.password}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleCopyToClipboard(selectedCredentials.password, 'Password')}
                  className="ml-2"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-xs text-yellow-800">
                  <strong>Security Note:</strong> Please share these credentials securely with the staff member. 
                  Advise them to change their password after first login.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => setCredentialsModalOpen(false)} className="bg-[#0056B3] hover:bg-[#0056B3]/90">
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Staff Topics Modal */}
        <Dialog open={topicsModalOpen} onOpenChange={setTopicsModalOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle>View Chapters</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto min-h-0 mt-4">
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-1">Staff Member</p>
                    <p className="text-base font-semibold text-gray-900">{selectedStaffTopics.employee_name}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-1">Job Role</p>
                    <p className="text-base font-semibold text-gray-900">{selectedStaffTopics.job_function_name}</p>
                  </div>
                </div>
              </div>
              
              {topicsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#0056B3]" />
                </div>
              ) : selectedStaffTopics.assigned_topics.length === 0 ? (
                <div className="text-center py-8">
                  <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No chapters found</p>
                  <p className="text-sm text-gray-400 mt-1">Topics are assigned based on their job role.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {selectedStaffTopics.assigned_topics.map((topic, topicIndex) => (
                    <div key={topic.topic_id} className="border border-gray-200 rounded-lg p-4 bg-white">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-semibold text-blue-600">{topicIndex + 1}</span>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <h4 className="font-semibold text-gray-900 text-base">{topic.topic_title}</h4>
                            <div className="flex items-center gap-2">
                              <Badge className={`text-xs ${
                                topic.status === 'Completed' ? 'bg-green-100 text-green-700 border-green-200' :
                                topic.status === 'In Progress' ? 'bg-amber-100 text-amber-700 border-amber-200' :
                                'bg-gray-100 text-gray-600 border-gray-200'
                              }`}>
                                {topic.status}
                              </Badge>
                              {canManageStaffConfig && (topic.status === 'Completed' || topic.status === 'In Progress') && (
                                <Button
                                  type="button" variant="outline" size="sm"
                                  className="h-7 px-2 text-xs text-orange-600 border-orange-300 hover:bg-orange-50"
                                  disabled={resettingTopicId === topic.topic_id}
                                  onClick={async () => {
                                    if (!window.confirm(`Reset chapter "${topic.topic_title}" for ${selectedStaffTopics.employee_name}? This will clear all progress and the staff member will need to go through it again.`)) return;
                                    setResettingTopicId(topic.topic_id);
                                    try {
                                      const token = localStorage.getItem('token');
                                      await axios.post(
                                        `${API}/hospital-config/employees/${selectedStaffEmployeeId}/topics/${topic.topic_id}/reset`,
                                        {},
                                        { headers: { Authorization: `Bearer ${token}` } }
                                      );
                                      toast.success('Chapter progress reset successfully');
                                      // Refresh topics list
                                      const resp = await axios.get(`${API}/hospital-config/employees/${selectedStaffEmployeeId}/topics`, {
                                        headers: { Authorization: `Bearer ${token}` }
                                      });
                                      setSelectedStaffTopics({
                                        employee_name: resp.data.employee_name,
                                        job_function_name: resp.data.job_function_name,
                                        assigned_topics: resp.data.assigned_topics || []
                                      });
                                    } catch (error) {
                                      toast.error(error.response?.data?.detail || 'Failed to reset chapter');
                                    } finally {
                                      setResettingTopicId(null);
                                    }
                                  }}
                                >
                                  {resettingTopicId === topic.topic_id ? (
                                    <Loader2 className="w-3 h-3 animate-spin mr-1" />
                                  ) : (
                                    <RotateCcw className="w-3 h-3 mr-1" />
                                  )}
                                  Reset Chapter
                                </Button>
                              )}
                            </div>
                          </div>
                          <p className="text-sm text-gray-500 mt-1">
                            {topic.sections.length} {topic.sections.length === 1 ? 'subchapter' : 'subchapters'}
                          </p>
                        </div>
                      </div>
                      
                      {topic.sections.length > 0 && (
                        <div className="ml-11 space-y-2">
                          {topic.sections.map((section, sectionIndex) => (
                            <div key={section.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                              <div className="w-6 h-6 rounded bg-green-100 flex items-center justify-center flex-shrink-0">
                                <span className="text-xs font-medium text-green-600">{sectionIndex + 1}</span>
                              </div>
                              <span className="text-sm text-gray-700">{section.title}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-800">
                  <strong>Note:</strong> Chapters and subchapters are assigned based on the staff member's job role. 
                  To modify assignments, please update the job role configuration.
                </p>
              </div>
            </div>
            </div>
            <DialogFooter>
              <Button onClick={() => setTopicsModalOpen(false)} className="bg-[#0056B3] hover:bg-[#0056B3]/90">
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
    </ResponsiveContainer>
    </AdminLayout>
  );
};

export default StaffManagementPage;
